import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type Language = 'EN' | 'HE' | 'AR' | 'RU'
export type ThemeMode = 'light' | 'dark'

export const LANG_DIRECTION: Record<Language, 'ltr' | 'rtl'> = {
  EN: 'ltr',
  HE: 'rtl',
  AR: 'rtl',
  RU: 'ltr',
}

export const LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'EN', label: 'English', nativeLabel: 'English' },
  { code: 'HE', label: 'Hebrew', nativeLabel: 'עברית' },
  { code: 'AR', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'RU', label: 'Russian', nativeLabel: 'Русский' },
]

const STORAGE_KEY = 'logargo.admin.settings.v1'
const LEGACY_LANGUAGE_KEY = 'logargo.language.v1'
const LEGACY_THEME_KEY = 'theme-mode'

function isLanguage(value: unknown): value is Language {
  return value === 'EN' || value === 'HE' || value === 'AR' || value === 'RU'
}

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

/** One-time import from older per-key localStorage entries. */
function migrateLegacySettings(): void {
  if (localStorage.getItem(STORAGE_KEY)) return

  let language: Language = 'EN'
  let themeMode: ThemeMode = 'light'

  try {
    const langRaw = localStorage.getItem(LEGACY_LANGUAGE_KEY)
    if (langRaw) {
      const parsed = JSON.parse(langRaw) as { state?: { language?: unknown } }
      if (isLanguage(parsed.state?.language)) {
        language = parsed.state.language
      }
    }
  } catch {
    /* ignore corrupt legacy payload */
  }

  const legacyTheme = localStorage.getItem(LEGACY_THEME_KEY)
  if (isThemeMode(legacyTheme)) {
    themeMode = legacyTheme
  }

  const hadLegacyPreference =
    localStorage.getItem(LEGACY_LANGUAGE_KEY) != null || legacyTheme != null

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        state: {
          language,
          themeMode,
          preferencesCustomized: hadLegacyPreference,
        },
        version: 0,
      }),
    )
  } catch {
    /* quota / private mode */
  }
}

migrateLegacySettings()

const DEFAULT_SETTINGS = { language: 'EN' as Language, themeMode: 'light' as ThemeMode }

/** Synchronous read used before zustand rehydrates (i18n init, default store state). */
export function readStoredSettings(): {
  language: Language
  themeMode: ThemeMode
  preferencesCustomized: boolean
} {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as {
        state?: { language?: unknown; themeMode?: unknown; preferencesCustomized?: unknown }
      }
      return {
        language: isLanguage(parsed.state?.language) ? parsed.state.language : DEFAULT_SETTINGS.language,
        themeMode: isThemeMode(parsed.state?.themeMode) ? parsed.state.themeMode : DEFAULT_SETTINGS.themeMode,
        preferencesCustomized: parsed.state?.preferencesCustomized === true,
      }
    }
  } catch {
    /* ignore corrupt payload */
  }

  return { ...DEFAULT_SETTINGS, preferencesCustomized: false }
}

const initialSettings = readStoredSettings()

interface SettingsState {
  language: Language
  themeMode: ThemeMode
  preferencesCustomized: boolean
  setLanguage: (language: Language) => void
  setThemeMode: (themeMode: ThemeMode) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      language: initialSettings.language,
      themeMode: initialSettings.themeMode,
      preferencesCustomized: initialSettings.preferencesCustomized,
      setLanguage: (language) => set({ language, preferencesCustomized: true }),
      setThemeMode: (themeMode) => set({ themeMode, preferencesCustomized: true }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (!state?.language) return
        void import('../i18n').then(({ default: i18n }) => {
          const lng = state.language.toLowerCase()
          if (i18n.language !== lng) {
            i18n.changeLanguage(lng)
          }
        })
      },
    },
  ),
)

/** True when the user has explicitly saved UI preferences (language or theme). */
export function hasStoredLanguagePreference(): boolean {
  return readStoredSettings().preferencesCustomized
}
